// frontend/src/pages/OnboardingPage.tsx
//
// Multi-step onboarding flow — starts AFTER registration.
// Collects: source language → target language → proficiency level.
// Calls POST /api/v1/onboarding/ on completion, then navigates to /home.
//
// Step 4 slot is intentionally vacant — reserved for the UX_SCENARIO §2
// "Where should your words come from?" source fork.
//
// Styling: inline CSS tokens (consistent with the rest of the codebase).
// Animations: Framer Motion slide transitions between steps.

import { useState, useCallback, useMemo, useEffect, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import apiClient, { extractErrorMessage } from '../api/client'
import { isLanguageNotReleased } from '../utils/languageWriteError'
import { useAuthStore, selectSetUser } from '../stores/authStore'
import { trackEvent } from '../utils/analytics'
import { useLocale } from '../i18n/context'
import { resolveLocale } from '../i18n/app'
import { safeReturnTo } from '../utils/returnTo'
import {
  browserLanguages,
  matchBrowserLanguage,
  selectableInterfaceLanguages,
  useLanguageRegistry,
  type RegistryLanguage,
} from '../i18n/registry'
import {
  ONBOARDING_TARGET_LANGUAGES,
  SUPPORTED_LANGUAGE_PAIRS,
} from '../utils/languages'

// ─── Constants ────────────────────────────────────────────────────────────────

const LEVELS = [
  { code: 'A1', label: 'Beginner',           desc: 'I know almost nothing' },
  { code: 'A2', label: 'Elementary',         desc: 'I can use simple phrases' },
  { code: 'B1', label: 'Intermediate',       desc: 'I can hold basic conversations' },
  { code: 'B2', label: 'Upper Intermediate', desc: 'I can discuss most topics' },
] as const

// Placement tests cannot reliably distinguish levels above B2.
const PLACEMENT_MAX_LEVEL = 'B2'
const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
function clampToPlacementMax(level: string): string {
  const idx = CEFR_ORDER.indexOf(level)
  const maxIdx = CEFR_ORDER.indexOf(PLACEMENT_MAX_LEVEL)
  return idx > maxIdx ? PLACEMENT_MAX_LEVEL : level
}

// Steps: 0 = Welcome, 1 = SourceLang, 2 = TargetLang, 3 = Level
// Progress dots are shown for steps 1–3 (3 dots total).
const TOTAL_STEPS = 3

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Native languages offered at step 1. Onboarding sets the interface to the
 * chosen native language, so only a language with an interface table is
 * offered (ADR-111 D4), and only once released (D5). English comes last: a
 * native speaker of English learning English is the special, self-pair case.
 */
function onboardingSourceLanguages(registry: RegistryLanguage[] | null): RegistryLanguage[] {
  const offered = selectableInterfaceLanguages(registry)
  return [...offered.filter((l) => l.code !== 'en'), ...offered.filter((l) => l.code === 'en')]
}

// ─── Framer Motion variants (defined inside component to respect reduced motion) ─

// ─── Shared UI ─────────────────────────────────────────────────────────────────

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Go back"
      style={{
        width: 44,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        backgroundColor: 'var(--bg-surface)',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 18-6-6 6-6" />
      </svg>
    </button>
  )
}

function PrimaryButton({
  onClick,
  disabled = false,
  loading = false,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        ...primaryBtnStyle,
        opacity: disabled || loading ? 0.45 : 1,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        touchAction: 'manipulation',
      }}
    >
      {loading ? (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <SpinnerIcon />
          <LoadingLabel />
        </span>
      ) : children}
    </button>
  )
}

function SpinnerIcon() {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      style={{ animation: 'spin 0.8s linear infinite' }}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  )
}

function LoadingLabel() {
  const { tr } = useLocale()
  return <>{tr.onboarding.settingUp}</>
}

function ProgressDots({ current }: { current: number }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const done   = i < current - 1
        const active = i === current - 1
        return (
          <motion.div
            key={i}
            animate={{
              width: active ? 20 : 8,
              backgroundColor: active || done ? 'var(--accent)' : 'var(--border)',
              opacity: active || done ? 1 : 0.5,
            }}
            transition={{ duration: 0.22 }}
            style={{ height: 8, borderRadius: 4 }}
          />
        )
      })}
    </div>
  )
}

// Shared language/option button
function OptionButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 16px',
        borderRadius: 'var(--radius-md)',
        border: `2px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
        backgroundColor: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
        minHeight: 56,
        transition: 'border-color 0.15s',
        fontFamily: 'var(--font-ui)',
        touchAction: 'manipulation',
      }}
    >
      {children}
    </button>
  )
}

function CheckIcon() {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="var(--accent)" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ marginLeft: 'auto', flexShrink: 0 }}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

// ─── Placement test v2 (ADR-051) ──────────────────────────────────────────────
// Adaptive Yes/No recognition test with pseudoword foils.
// Phases: disclosure → test → result (→ onComplete or onBack)

interface PlacementItem {
  id: string
  word: string
}

type PlacementPhase = 'loading' | 'disclosure' | 'test' | 'submitting' | 'result' | 'error'

interface PlacementTestResult {
  estimated_level: string
  low_confidence: boolean
  false_alarm_rate: number
}

function PlacementTestStep({
  onBack,
  onComplete,
}: {
  onBack: () => void
  onComplete: (estimatedLevel: string) => void
}) {
  const { tr } = useLocale()
  const [phase, setPhase] = useState<PlacementPhase>('loading')
  const [items, setItems] = useState<PlacementItem[]>([])
  // Map of item_id → said_yes (built up as the user answers)
  const [responses, setResponses] = useState<Map<string, boolean>>(new Map())
  const [currentIndex, setCurrentIndex] = useState(0)
  const [result, setResult] = useState<PlacementTestResult | null>(null)

  useEffect(() => {
    let cancelled = false
    apiClient
      .get<{ items: PlacementItem[]; total: number }>('/api/v1/onboarding/placement/words/')
      .then(({ data }) => {
        if (cancelled) return
        if (data.total === 0) { setPhase('error'); return }
        setItems(data.items)
        setPhase('disclosure')
      })
      .catch(() => { if (!cancelled) setPhase('error') })
    return () => { cancelled = true }
  }, [])

  const handleAnswer = useCallback((saidYes: boolean) => {
    const item = items[currentIndex]
    if (!item) return
    setResponses(prev => new Map(prev).set(item.id, saidYes))
    if (currentIndex + 1 < items.length) {
      setCurrentIndex(i => i + 1)
    } else {
      // All items answered — submit
      const finalResponses = new Map(responses).set(item.id, saidYes)
      const results = items.map(it => ({ item_id: it.id, said_yes: finalResponses.get(it.id) ?? false }))
      setPhase('submitting')
      apiClient
        .post<PlacementTestResult>('/api/v1/onboarding/placement/', { results })
        .then(({ data }) => { setResult(data); setPhase('result') })
        .catch(() => setPhase('error'))
    }
  }, [items, currentIndex, responses])

  // ── Loading ────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{ ...stepContainerStyle, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <SpinnerIcon />
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
          {tr.onboarding.placementLoading}
        </p>
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div style={{ ...stepContainerStyle, alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', margin: 0 }}>
          {tr.onboarding.placementEmpty}
        </p>
        <button onClick={onBack} style={{ ...primaryBtnStyle, width: 'auto', padding: '12px 32px' }}>
          {tr.onboarding.placementBack}
        </button>
      </div>
    )
  }

  // ── Disclosure ─────────────────────────────────────────────────────────────
  if (phase === 'disclosure') {
    return (
      <div style={{ ...stepContainerStyle, justifyContent: 'space-between' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 20 }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            backgroundColor: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
              stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </div>
          <div>
            <h2 style={stepTitleStyle}>{tr.onboarding.placementDisclosureTitle}</h2>
            <p style={{ ...stepSubtitleStyle, marginTop: 8 }}>
              {tr.onboarding.placementDisclosureBody}
            </p>
          </div>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PrimaryButton onClick={() => { setCurrentIndex(0); setResponses(new Map()); setPhase('test') }}>
            {tr.onboarding.placementDisclosureStart}
          </PrimaryButton>
          <button
            onClick={onBack}
            style={{
              width: '100%', padding: '10px 24px', backgroundColor: 'transparent',
              border: 'none', color: 'var(--text-secondary)', fontSize: 14,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-ui)',
              minHeight: 44, touchAction: 'manipulation',
            }}
          >
            {tr.onboarding.placementBack}
          </button>
        </div>
      </div>
    )
  }

  // ── Test (one word at a time) ──────────────────────────────────────────────
  if (phase === 'test' || phase === 'submitting') {
    const total = items.length
    const current = Math.min(currentIndex + 1, total)
    const progress = total > 0 ? (currentIndex / total) * 100 : 0
    const currentWord = items[currentIndex]?.word ?? ''
    const isSubmitting = phase === 'submitting'

    return (
      <div style={{ ...stepContainerStyle, gap: 24 }}>
        {/* Progress bar */}
        <div style={{ flexShrink: 0 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 6,
            fontSize: 12,
            color: 'var(--text-secondary)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {tr.onboarding.placementItemProgress(current, total)}
          </div>
          <div style={{
            height: 3,
            backgroundColor: 'var(--border)',
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: 'var(--accent)',
              borderRadius: 2,
              transition: 'width 0.2s ease',
            }} />
          </div>
        </div>

        {/* Word card */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-flashcard)',
            padding: '40px 32px',
            textAlign: 'center',
            width: '100%',
          }}>
            <p style={{
              fontSize: 32,
              fontWeight: 700,
              fontFamily: 'var(--font-card)',
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              margin: 0,
              lineHeight: 1.2,
            }}>
              {currentWord}
            </p>
          </div>
        </div>

        {/* Yes / No buttons */}
        <div style={{ flexShrink: 0, display: 'flex', gap: 12 }}>
          <button
            onClick={() => !isSubmitting && handleAnswer(false)}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '16px 12px',
              borderRadius: 'var(--radius-md)',
              border: '2px solid var(--border)',
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-secondary)',
              fontSize: 15,
              fontWeight: 600,
              cursor: isSubmitting ? 'default' : 'pointer',
              fontFamily: 'var(--font-ui)',
              minHeight: 56,
              touchAction: 'manipulation',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'border-color 0.12s, background-color 0.12s',
            }}
          >
            {tr.onboarding.placementNo}
          </button>
          <button
            onClick={() => !isSubmitting && handleAnswer(true)}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '16px 12px',
              borderRadius: 'var(--radius-md)',
              border: '2px solid var(--accent-fill)',
              backgroundColor: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              fontSize: 15,
              fontWeight: 600,
              cursor: isSubmitting ? 'default' : 'pointer',
              fontFamily: 'var(--font-ui)',
              minHeight: 56,
              touchAction: 'manipulation',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'opacity 0.12s',
            }}
          >
            {isSubmitting ? '…' : tr.onboarding.placementYes}
          </button>
        </div>
      </div>
    )
  }

  // ── Result ─────────────────────────────────────────────────────────────────
  if (phase === 'result' && result) {
    return (
      <div style={{ ...stepContainerStyle, justifyContent: 'space-between' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 20 }}>
          <div>
            <p style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
              margin: '0 0 8px',
            }}>
              {tr.onboarding.placementResultTitle}
            </p>
            <h2 style={{ ...stepTitleStyle, fontSize: 48, margin: 0, lineHeight: 1 }}>
              {result.estimated_level}
            </h2>
            <p style={{ ...stepSubtitleStyle, marginTop: 10 }}>
              {tr.onboarding.placementResultSub(result.estimated_level)}
            </p>
          </div>
          {result.low_confidence && (
            <div style={{
              backgroundColor: 'var(--warning-soft, color-mix(in srgb, var(--accent) 10%, transparent))',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 14px',
              fontSize: 13,
              color: 'var(--text-secondary)',
              lineHeight: 1.4,
            }}>
              {tr.onboarding.placementResultLowConfidence}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PrimaryButton onClick={() => onComplete(result.estimated_level)}>
            {tr.onboarding.placementResultConfirm}
          </PrimaryButton>
          <button
            onClick={onBack}
            style={{
              width: '100%', padding: '10px 24px', backgroundColor: 'transparent',
              border: 'none', color: 'var(--text-secondary)', fontSize: 14,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-ui)',
              minHeight: 44, touchAction: 'manipulation',
            }}
          >
            {tr.onboarding.placementResultManual}
          </button>
        </div>
      </div>
    )
  }

  return null
}

// ─── Step: Welcome ─────────────────────────────────────────────────────────────

function WelcomeStep({ onNext }: { onNext: () => void }) {
  const { tr } = useLocale()
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '0 12px 48px',
    }}>
      {/* App icon */}
      <div style={{
        width: 80,
        height: 80,
        borderRadius: 24,
        backgroundColor: 'var(--accent-fill)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
      }}>
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
          stroke="var(--text-on-dark)" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M8 10h8M8 14h4" />
        </svg>
      </div>

      {/* Brand label */}
      <p style={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--accent)',
        marginBottom: 16,
      }}>
        {'CardSmith' /* i18n-exempt: brand name */}
      </p>

      {/* Headline */}
      <h1 style={{
        fontSize: 36,
        fontWeight: 800,
        lineHeight: 1.15,
        color: 'var(--text-primary)',
        fontFamily: "'Source Serif 4', serif",
        letterSpacing: '-0.02em',
        marginBottom: 16,
        maxWidth: 300,
      }}>
        {tr.onboarding.welcomeHeadline}
      </h1>

      <p style={{
        fontSize: 15,
        lineHeight: 1.6,
        color: 'var(--text-secondary)',
        marginBottom: 48,
        maxWidth: 280,
      }}>
        {tr.onboarding.welcomeSub}
      </p>

      <div style={{ width: '100%', maxWidth: 320 }}>
        <PrimaryButton onClick={onNext}>{tr.onboarding.getStarted}</PrimaryButton>
      </div>
    </div>
  )
}

// ─── Step: Source Language ─────────────────────────────────────────────────────

function SourceLanguageStep({
  options,
  selected,
  onSelect,
  onNext,
}: {
  options: RegistryLanguage[]
  selected: string
  onSelect: (code: string) => void
  onNext: () => void
}) {
  const { tr } = useLocale()
  return (
    <div style={stepContainerStyle}>
      <div>
        <h2 style={stepTitleStyle}>{tr.onboarding.sourceLangTitle}</h2>
        <p style={stepSubtitleStyle}>{tr.onboarding.sourceLangSub}</p>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(lang => (
          <OptionButton key={lang.code} selected={selected === lang.code} onClick={() => onSelect(lang.code)}>
            <span style={{ fontWeight: 500, fontSize: 17, flex: 1 }}>{lang.nativeName}</span>
            {selected === lang.code && <CheckIcon />}
          </OptionButton>
        ))}
      </div>

      <div style={{ paddingTop: 16, flexShrink: 0 }}>
        <PrimaryButton onClick={onNext} disabled={!selected}>{tr.onboarding.continue}</PrimaryButton>
      </div>
    </div>
  )
}

// ─── Step: Target Language ─────────────────────────────────────────────────────

function TargetLanguageStep({
  selected,
  onSelect,
  onNext,
}: {
  selected: string
  onSelect: (code: string) => void
  onNext: () => void
}) {
  const { tr } = useLocale()
  return (
    <div style={stepContainerStyle}>
      <div>
        <h2 style={stepTitleStyle}>{tr.onboarding.targetLangTitle}</h2>
        <p style={stepSubtitleStyle}>{tr.onboarding.targetLangSub}</p>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ONBOARDING_TARGET_LANGUAGES.map(lang => {
          if (lang.available) {
            return (
              <OptionButton key={lang.code} selected={selected === lang.code} onClick={() => onSelect(lang.code)}>
                <span style={{ fontWeight: 500, fontSize: 17, flex: 1 }}>{lang.nativeName}</span>
                {selected === lang.code && <CheckIcon />}
              </OptionButton>
            )
          }
          // Disabled / coming-soon entry — not a button, just a visual placeholder
          return (
            <div
              key={lang.code}
              aria-disabled="true"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                border: '2px solid var(--border)',
                backgroundColor: 'var(--bg-surface)',
                opacity: 0.45,
                minHeight: 56,
                boxSizing: 'border-box',
              }}
            >
              <span style={{ fontWeight: 500, fontSize: 17, color: 'var(--text-primary)', flex: 1 }}>
                {lang.nativeName}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--text-secondary)',
                flexShrink: 0,
              }}>
                {tr.onboarding.comingSoon}
              </span>
            </div>
          )
        })}
      </div>

      <div style={{ paddingTop: 16, flexShrink: 0 }}>
        <PrimaryButton onClick={onNext} disabled={!selected}>{tr.onboarding.continue}</PrimaryButton>
      </div>
    </div>
  )
}

// ─── Step: Level ───────────────────────────────────────────────────────────────

function LevelStep({
  targetLanguageName,
  selected,
  onSelect,
  onSubmit,
  submitLoading,
  onStartPlacement,
  placementEstimate,
  isPairSupported,
  showPlacementTest,
}: {
  targetLanguageName: string
  selected: string
  onSelect: (code: string) => void
  onSubmit: () => void
  submitLoading: boolean
  onStartPlacement: () => void
  placementEstimate: string | null
  isPairSupported: boolean
  showPlacementTest: boolean
}) {
  const { tr } = useLocale()
  return (
    <div style={stepContainerStyle}>
      <div>
        <h2 style={stepTitleStyle}>{tr.onboarding.levelTitle(targetLanguageName)}</h2>
        <p style={stepSubtitleStyle}>{tr.onboarding.levelSub}</p>
      </div>

      {!isPairSupported && (
        <div style={{
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1.5px solid var(--danger)',
          backgroundColor: 'var(--danger-soft)',
          fontSize: 13,
          color: 'var(--danger)',
          fontWeight: 500,
          flexShrink: 0,
        }}>
          {tr.onboarding.unsupportedPair}
        </div>
      )}

      {placementEstimate && (
        <div style={{
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1.5px solid var(--accent)',
          backgroundColor: 'var(--accent-soft)',
          fontSize: 13,
          color: 'var(--accent)',
          fontWeight: 500,
          flexShrink: 0,
        }}>
          {tr.onboarding.placementResultBanner(placementEstimate)}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {LEVELS.map(lv => {
          const active = selected === lv.code
          const lvTr = tr.onboarding.levels[lv.code]
          return (
            <button
              key={lv.code}
              onClick={() => onSelect(lv.code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                width: '100%',
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                textAlign: 'left',
                minHeight: 68,
                transition: 'border-color 0.15s',
                fontFamily: 'var(--font-ui)',
                touchAction: 'manipulation',
              }}
            >
              {/* CEFR badge */}
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 'var(--radius-sm)',
                backgroundColor: active ? 'var(--accent-fill)' : 'var(--bg-page)',
                color: active ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.03em',
                flexShrink: 0,
                transition: 'background-color 0.15s, color 0.15s',
              }}>
                {lv.code}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{lvTr.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{lvTr.desc}</div>
              </div>

              {active && <CheckIcon />}
            </button>
          )
        })}
      </div>

      <div style={{ paddingTop: 16, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <PrimaryButton onClick={onSubmit} disabled={!selected || !isPairSupported} loading={submitLoading}>
          {tr.onboarding.startLearning}
        </PrimaryButton>

        {/* Placement test CTA — shown for all supported pairs (ADR-051 amendment) */}
        {isPairSupported && showPlacementTest && (
          <>
            {/* "or" divider — keeps hierarchy clear: direct-pick is the default path */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '2px 0',
            }}>
              <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, flexShrink: 0 }}>
                {tr.onboarding.orSeparator}
              </span>
              <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
            </div>

            <button
              onClick={onStartPlacement}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: 'var(--bg-secondary)',
                border: '1.5px solid var(--border)',
                borderRadius: 'var(--radius-pill)',
                color: 'var(--text-secondary)',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                minHeight: 48,
                touchAction: 'manipulation',
                transition: 'border-color 0.15s, color 0.15s',
              }}
            >
              {tr.onboarding.levelNotSure}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Demo language suggestion (from landing-page CTA → register flow) ─────────

const DEMO_LANG_KEY = 'cs-demo-lang'

const VALID_TARGET_CODES = new Set<string>(ONBOARDING_TARGET_LANGUAGES.filter(l => l.available).map(l => l.code))

function readDemoLangSuggestion(): string | null {
  try {
    const lang = sessionStorage.getItem(DEMO_LANG_KEY)
    return lang && VALID_TARGET_CODES.has(lang) ? lang : null
  } catch { return null }
}

// ─── localStorage progress persistence ────────────────────────────────────────

const PROGRESS_KEY = 'cardsmith-onboarding-progress'

interface SavedProgress {
  step: number
  sourceLanguage: string
  targetLanguage: string
  level: string
}

function loadProgress(): SavedProgress | null {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY)
    return raw ? (JSON.parse(raw) as SavedProgress) : null
  } catch { return null }
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setUser = useAuthStore(selectSetUser)
  const prefersReducedMotion = useReducedMotion()
  const { setLocale, tr } = useLocale()
  const onboardingErrorFallback = tr.onboarding.error

  // Lazy initialisers so localStorage is only read once (on mount).
  const [step, setStep]           = useState(() => loadProgress()?.step ?? 0)
  const [direction, setDirection] = useState(1)        // 1 = forward, -1 = back

  // Slide variants: reduced-motion users get a simple fade instead of slide
  const slideVariants = useMemo(() => ({
    enter: (dir: number) => ({
      x: prefersReducedMotion ? 0 : dir > 0 ? 56 : -56,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: prefersReducedMotion ? 0.15 : 0.3,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      },
    },
    exit: (dir: number) => ({
      x: prefersReducedMotion ? 0 : dir > 0 ? -56 : 56,
      opacity: 0,
      transition: {
        duration: prefersReducedMotion ? 0.1 : 0.2,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      },
    }),
  }), [prefersReducedMotion])

  const registry = useLanguageRegistry()
  const sourceOptions = useMemo(() => onboardingSourceLanguages(registry), [registry])
  // Until the user picks, the browser language among the offered ones (else English).
  const [chosenSource, setSourceLanguage] = useState<string | null>(() => loadProgress()?.sourceLanguage ?? null)
  const sourceLanguage = chosenSource ?? matchBrowserLanguage(browserLanguages(), sourceOptions.map((l) => l.code))
  const [targetLanguage, setTargetLanguage]   = useState<string>(() => loadProgress()?.targetLanguage ?? readDemoLangSuggestion() ?? 'en')
  const [level, setLevel]                     = useState<string>(() => loadProgress()?.level ?? '')

  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitError, setSubmitError]     = useState<string | null>(null)

  // Placement test sub-flow state (lives inside step 3; doesn't add a progress dot)
  const [showingPlacement, setShowingPlacement]     = useState(false)
  const [placementEstimate, setPlacementEstimate]   = useState<string | null>(null)


  // Persist progress so a page refresh mid-onboarding doesn't lose selections.
  useEffect(() => {
    if (step === 0) return // don't bother saving before user has started
    try {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify({
        step, sourceLanguage, targetLanguage, level,
      }))
    } catch { /* ignore quota errors */ }
  }, [step, sourceLanguage, targetLanguage, level])

  const handleSourceLangSelect = useCallback((code: string) => {
    setSourceLanguage(code)
    setLocale(resolveLocale(code))
  }, [setLocale])

  const goNext = useCallback(() => {
    setDirection(1)
    setStep(s => Math.min(s + 1, 3))
    setSubmitError(null)
  }, [])

  const goBack = useCallback(() => {
    setDirection(-1)
    setStep(s => Math.max(s - 1, 0))
    setSubmitError(null)
  }, [])

  const isPairSupported = SUPPORTED_LANGUAGE_PAIRS.includes(`${sourceLanguage}-${targetLanguage}`)
  // ADR-051 amendment (2026-07-13): pseudoword mechanic is valid for same-language pairs too;
  // show for all supported pairs.
  const showPlacementTest = true

  const handleSubmit = useCallback(async () => {
    if (!SUPPORTED_LANGUAGE_PAIRS.includes(`${sourceLanguage}-${targetLanguage}`)) {
      setSubmitError(tr.onboarding.unsupportedPair)
      return
    }
    setSubmitLoading(true)
    setSubmitError(null)
    try {
      await apiClient.post('/api/v1/onboarding/', {
        source_language: sourceLanguage,
        target_language: targetLanguage,
        current_level: level,
      })
      // Persist the UI language the user experienced during onboarding
      await apiClient.patch('/api/v1/auth/me', { interface_language: sourceLanguage })
      // Refresh user so onboarding_completed reflects true
      const meRes = await apiClient.get('/api/v1/auth/me')
      setUser(meRes.data)
      // Clear saved progress and demo suggestion now that onboarding is done
      try { localStorage.removeItem(PROGRESS_KEY) } catch { /* ignore */ }
      try { sessionStorage.removeItem(DEMO_LANG_KEY) } catch { /* ignore */ }
      trackEvent('onboarding_completed', {
        target_language: targetLanguage,
        source_language: sourceLanguage,
        level,
      })
      navigate(safeReturnTo(searchParams.get('returnTo')), { replace: true })
    } catch (err: unknown) {
      setSubmitError(
        isLanguageNotReleased(err)
          ? tr.onboarding.languageNotReleased
          : extractErrorMessage(err, onboardingErrorFallback),
      )
    } finally {
      setSubmitLoading(false)
    }
  }, [sourceLanguage, targetLanguage, level, setUser, navigate, onboardingErrorFallback, tr])

  const targetLangName =
    ONBOARDING_TARGET_LANGUAGES.find(l => l.code === targetLanguage)?.nativeName ?? 'your target language'

  return (
    <div style={{
      height: '100svh',
      overflow: 'hidden',
      backgroundColor: 'var(--bg-page)',
      color: 'var(--text-primary)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'var(--font-ui)',
    }}>
      {/* ── Header bar ── */}
      <div style={{
        height: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        flexShrink: 0,
      }}>
        {/* Back button (hidden on Welcome).  In placement sub-flow, exits placement
            rather than going back to the previous step. */}
        <div style={{ width: 44 }}>
          {(step > 0 || showingPlacement) && (
            <BackButton onClick={showingPlacement
              ? () => { setDirection(-1); setShowingPlacement(false) }
              : goBack
            } />
          )}
        </div>

        {/* Progress dots (hidden on Welcome) */}
        {step > 0 && <ProgressDots current={step} />}

        {/* Right spacer */}
        <div style={{ width: 44 }} />
      </div>

      {/* ── Animated step content ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <AnimatePresence custom={direction} initial={false} mode="wait">
          <motion.div
            key={step === 3 && showingPlacement ? '3-placement' : step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 20px calc(24px + env(safe-area-inset-bottom, 0px))',
              maxWidth: 480,
              width: '100%',
              margin: '0 auto',
              willChange: 'transform, opacity',
            }}
          >
            {step === 0 && (
              <WelcomeStep onNext={goNext} />
            )}
            {step === 1 && (
              <SourceLanguageStep
                options={sourceOptions}
                selected={sourceLanguage}
                onSelect={handleSourceLangSelect}
                onNext={goNext}
              />
            )}
            {step === 2 && (
              <TargetLanguageStep
                selected={targetLanguage}
                onSelect={setTargetLanguage}
                onNext={goNext}
              />
            )}
            {step === 3 && !showingPlacement && (
              <LevelStep
                targetLanguageName={targetLangName}
                selected={level}
                onSelect={setLevel}
                onSubmit={handleSubmit}
                submitLoading={submitLoading}
                onStartPlacement={() => { setDirection(1); setPlacementEstimate(null); setShowingPlacement(true) }}
                placementEstimate={placementEstimate}
                isPairSupported={isPairSupported}
                showPlacementTest={showPlacementTest}
              />
            )}
            {step === 3 && showingPlacement && (
              <PlacementTestStep
                onBack={() => { setDirection(-1); setShowingPlacement(false) }}
                onComplete={(estimatedLevel) => {
                  const clamped = clampToPlacementMax(estimatedLevel)
                  setLevel(clamped)
                  setPlacementEstimate(clamped)
                  setDirection(-1)
                  setShowingPlacement(false)
                }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Submit error ── */}
      {submitError && (
        <div style={{
          padding: '0 20px 16px',
          flexShrink: 0,
        }}>
          <div style={{
            backgroundColor: 'var(--danger-soft)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            color: 'var(--danger)',
            fontSize: 13,
            textAlign: 'center',
          }}>
            {submitError}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Shared style objects ──────────────────────────────────────────────────────

const stepContainerStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}

const stepTitleStyle: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  color: 'var(--text-primary)',
  letterSpacing: '-0.02em',
  marginBottom: 6,
}

const stepSubtitleStyle: CSSProperties = {
  fontSize: 14,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
}

const primaryBtnStyle: CSSProperties = {
  backgroundColor: 'var(--accent-fill)',
  color: 'var(--text-on-accent)',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '14px 24px',
  fontSize: 15,
  fontWeight: 600,
  width: '100%',
  minHeight: 52,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
  transition: 'opacity 0.15s',
}
